import {
	createInputAdjustmentTableFormShape,
	defaultInputAdjustmentTableFormValues,
} from '@dealstack/frontend/app/pages/Valuations/constants';
import {
	CreateInputAdjustmentTableForm,
	SavedInputTableForm,
} from '@dealstack/frontend/app/pages/Valuations/constants.types';
import { ValuationInputDetails } from '@dealstack/frontend/app/pages/Valuations/shared/valuationInputDetails.types';
import {
	computeValuationInputDetails,
	mapApiResponseToSavedForms,
	mapInputToSavedForm,
	mapSavedToInputForm,
} from '@dealstack/frontend/app/pages/Valuations/views/EditValuation/ValuationsInputWrapper/valuationInputForm';
import { HighPrecisionDecimal } from '@dealstack/shared/lib/format/currency/highPrecisionDecimal';
import {
	UpsertValuationInputAdjustmentItemDto,
	UpsertValuationInputItemDto,
	ValuationInputWithAdjustmentsRes,
} from '@dealstack/shared/lib/generated';
import { zodResolver } from '@hookform/resolvers/zod';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ValuationInputsSavePayload } from '@dealstack/frontend/app/pages/Valuations/queries/useValuationInputsQuery';
import { VALUATION_ADJUSTMENT_CLASSIFIERS } from '@dealstack/shared/lib/common/business/valuationClassifiers';

interface UseValuationInputFormOptions {
	valuationInputsRaw: ValuationInputWithAdjustmentsRes[];
	convertAmount: (amount: HighPrecisionDecimal, fromCurrency: string) => HighPrecisionDecimal;
	currentNumberFactor: number;
	saveFormSnapshot: (snapshot: ValuationInputsSavePayload) => void;
}

function buildSnapshot(
	rawInputs: ValuationInputWithAdjustmentsRes[],
	formValues: Record<string, SavedInputTableForm>
): ValuationInputsSavePayload {
	const inputs: UpsertValuationInputItemDto[] = [];
	const adjustments: UpsertValuationInputAdjustmentItemDto[] = [];

	const collectAdjustments = (
		rows: SavedInputTableForm['cash'],
		valuationInputId: string,
		classifierId: string
	) => {
		for (const row of rows) {
			if (!row.name?.trim()) {
				continue;
			}
			adjustments.push({
				id: row.adjustmentId,
				valuationInputId,
				name: row.name,
				currency: row.value.currency,
				// HighPrecisionDecimal.toString() is display-formatted (commas,
				// 2dp rounding) — Number() of that is NaN. Use toNumber().
				currencyAmount: row.value.currencyAmount.toNumber(),
				classifierId,
			});
		}
	};

	for (const raw of rawInputs) {
		const fv = formValues[raw.id];
		if (!fv) {
			continue;
		}
		inputs.push({
			id: raw.id,
			inputType: fv.valuationInput.inputType,
			currency: fv.valuationInput.value.currency,
			currencyAmount: fv.valuationInput.value.currencyAmount.toFixed(5),
		});
		collectAdjustments(fv.cash, raw.id, VALUATION_ADJUSTMENT_CLASSIFIERS.CASH);
		collectAdjustments(
			fv.workingCapital,
			raw.id,
			VALUATION_ADJUSTMENT_CLASSIFIERS.WORKING_CAPITAL
		);
	}

	return { inputs, adjustments };
}

export function useValuationInputForm({
	valuationInputsRaw,
	convertAmount,
	currentNumberFactor,
	saveFormSnapshot,
}: UseValuationInputFormOptions) {
	const [selectedValuationInputId, setSelectedValuationInputId] = useState<string | null>(null);
	const previousSelectedValuationInputId = useRef<string | null>(null);
	const previousNumberFactor = useRef<number | null>(null);
	const [valuationInputFormValues, setValuationInputFormValues] = useState<
		Record<string, SavedInputTableForm>
	>(() => mapApiResponseToSavedForms(valuationInputsRaw));

	// Sync derived state during render (not in a useEffect) so the first paint
	// after inputs arrive already reflects them. An effect would commit one
	// render with empty form values, briefly flashing EmptyOwnershipStructure.
	const [previousRawInputs, setPreviousRawInputs] = useState(valuationInputsRaw);
	if (previousRawInputs !== valuationInputsRaw) {
		setPreviousRawInputs(valuationInputsRaw);
		setValuationInputFormValues(mapApiResponseToSavedForms(valuationInputsRaw));
	}
	const formValuesRef = useRef(valuationInputFormValues);
	formValuesRef.current = valuationInputFormValues;
	const subscription = useRef<(() => void) | null>(null);
	const debouncedRenderUpdateRef = useRef<ReturnType<typeof debounce> | null>(null);
	const isResettingFormRef = useRef(false);

	const rawInputsRef = useRef(valuationInputsRaw);
	rawInputsRef.current = valuationInputsRaw;

	const zodSchema = useMemo(
		() => createInputAdjustmentTableFormShape(currentNumberFactor),
		[currentNumberFactor]
	);

	const inputAdjustmentTableForm = useForm<CreateInputAdjustmentTableForm>({
		mode: 'onChange',
		resolver: zodResolver(zodSchema),
		defaultValues: defaultInputAdjustmentTableFormValues,
	});
	const { reset, subscribe } = inputAdjustmentTableForm;

	const valuationInputDetails = useMemo(() => {
		const details: ValuationInputDetails[] = [];
		for (const input of valuationInputsRaw) {
			const detail = computeValuationInputDetails(
				valuationInputFormValues,
				convertAmount,
				input
			);
			if (detail) {
				details.push(detail);
			}
		}
		details.sort((a, b) => a.name.localeCompare(b.name));
		return details;
	}, [valuationInputFormValues, convertAmount, valuationInputsRaw]);

	const triggerSave = useCallback(
		(nextValues: Record<string, SavedInputTableForm>) => {
			const snapshot = buildSnapshot(rawInputsRef.current, nextValues);
			if (snapshot.inputs.length === 0 && snapshot.adjustments.length === 0) {
				return;
			}
			saveFormSnapshot(snapshot);
		},
		[saveFormSnapshot]
	);

	useEffect(() => {
		const hasInputChanged =
			selectedValuationInputId !== previousSelectedValuationInputId.current;
		const hasFactorChanged = currentNumberFactor !== previousNumberFactor.current;

		if ((hasInputChanged || hasFactorChanged) && !!selectedValuationInputId) {
			subscription.current?.();
			subscription.current = null;
			debouncedRenderUpdateRef.current?.cancel();

			previousNumberFactor.current = currentNumberFactor;
			previousSelectedValuationInputId.current = selectedValuationInputId;

			// Persist state across different factor changes. See https://github.com/dealstack-io/dealstack-v1/pull/4205#discussion_r2661112840
			const savedFormValues = valuationInputFormValues[selectedValuationInputId];
			const factoredFormValues: CreateInputAdjustmentTableForm = mapSavedToInputForm(
				savedFormValues,
				currentNumberFactor
			);
			// reset() fires the subscribe callback synchronously with the new
			// values — guard around the call so we can swallow only that one,
			// not any later user change that happens to match the same values
			// (e.g. toggling a field off then back on).
			isResettingFormRef.current = true;
			reset(factoredFormValues, {
				keepDefaultValues: false,
				keepValues: false,
			});
			isResettingFormRef.current = false;

			// Throttle the parent re-render: setValuationInputFormValues bumps a
			// memo that ValuationsInputTable consumes, so firing it on every
			// keystroke destabilises the currently-focused input (Playwright's
			// fill() can't satisfy its stability check). Save scheduling stays
			// synchronous below so the autosave indicator reflects pending input
			// immediately.
			const debouncedRenderUpdate = debounce((next: Record<string, SavedInputTableForm>) => {
				setValuationInputFormValues(next);
			}, 200);
			debouncedRenderUpdateRef.current = debouncedRenderUpdate;

			subscription.current = subscribe({
				formState: { values: true },
				callback: ({ values }) => {
					if (isResettingFormRef.current) {
						return;
					}

					const { success } = zodSchema.safeParse(values);
					if (!success) {
						return;
					}

					const factoredValues: SavedInputTableForm = mapInputToSavedForm(
						values,
						currentNumberFactor
					);
					const next = {
						...formValuesRef.current,
						[values.valuationInput.inputId]: factoredValues,
					};
					triggerSave(next);
					debouncedRenderUpdate(next);
				},
			});
		}
	}, [
		reset,
		subscribe,
		zodSchema,
		selectedValuationInputId,
		valuationInputFormValues,
		currentNumberFactor,
		triggerSave,
	]);

	useEffect(() => {
		return () => {
			subscription.current?.();
			subscription.current = null;
			debouncedRenderUpdateRef.current?.cancel();
			debouncedRenderUpdateRef.current = null;
		};
	}, []);

	useEffect(() => {
		if (
			valuationInputDetails.length &&
			!valuationInputDetails.find((detail) => detail.id === selectedValuationInputId)
		) {
			setSelectedValuationInputId(valuationInputDetails[0].id);
		} else if (!valuationInputDetails.length) {
			setSelectedValuationInputId(null);
		}
	}, [valuationInputDetails, selectedValuationInputId]);

	const selectedValuationInputDetails = valuationInputDetails.find(
		(detail) => detail.id === selectedValuationInputId
	);

	return {
		selectedValuationInputId,
		setSelectedValuationInputId,
		selectedValuationInputDetails,
		valuationInputDetails,
		inputAdjustmentTableForm,
	};
}
